import { useAuth0 } from "@auth0/auth0-react";
import {
    Breadcrumb,
    BreadcrumbProps,
    Breadcrumbs,
    Button,
    Menu,
    MenuDivider,
    MenuItem,
    Navbar,
    NavbarDivider,
    NavbarGroup,
    Popover,
    Position,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { reset as resetAnalytics } from "analytics/analytics";
import { segmentTrackLogoutClicked } from "analytics/analyticTrackEvents";
import PlanDetailsDialog from "components/sidebar/PlanDetailsDialog";
import { recordAuthChange } from "infrastructure/lastAuthChange";
import TextLogo from "logo/textLogo";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IN_QUEUE_ICON, PROSPECTING_ICON } from "types/appIcons";
import { IN_QUEUE_PATH, PROSPECTING_PATH } from "../RouterApp";
import {
    PlatformAction,
    usePlatformActions,
    usePlatformBreadcrumbs
} from "./PlatformShellContext";
import "./PlatformTopBar.scss";

// analytics.js needs a moment to hand the logout event off before auth0
// navigates the page away, but logging out must never depend on analytics:
// whichever of the track callback and this grace period lands first wins.
const LOGOUT_TRACKING_GRACE_MS = 500;

export const PlatformTopBar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth0();
    const breadcrumbs = usePlatformBreadcrumbs();
    const actions = usePlatformActions();

    const [planDialogOpen, setPlanDialogOpen] = useState(false);

    const handleLogout = () => {
        let hasLoggedOut = false;
        const finishLogout = () => {
            if (hasLoggedOut) {
                return;
            }
            hasLoggedOut = true;
            // Drop the persisted Segment identity and account group before
            // auth0 tears the session down, so the next login on this browser
            // starts clean. Logging out must not depend on analytics, and
            // this is the only path to it: swallow a failure here (no Segment
            // global, storage access revoked mid-session) rather than leaving
            // the user logged in with no way to retry.
            try {
                resetAnalytics();
            } catch (error) {
                console.error("Failed to reset analytics on logout", error);
            }
            recordAuthChange(undefined);
            logout({ returnTo: window.location.origin });
        };
        window.setTimeout(finishLogout, LOGOUT_TRACKING_GRACE_MS);
        // Tracked before the reset above, so the event still carries the user
        // and account that is logging out.
        segmentTrackLogoutClicked(finishLogout);
    };

    const isProspecting = location.pathname.startsWith(`/${PROSPECTING_PATH}`);
    const isInQueue = location.pathname.startsWith(`/${IN_QUEUE_PATH}`);

    const currentAppLabel = isProspecting
        ? "Prospecting"
        : isInQueue
        ? "In-Queue"
        : undefined;
    const currentAppIcon = isProspecting
        ? PROSPECTING_ICON
        : isInQueue
        ? IN_QUEUE_ICON
        : "grid-view";

    return (
        <>
            <Navbar className="PlatformTopBar">
                <NavbarGroup>
                    <Link to="/">
                        <TextLogo width={70} />
                    </Link>
                    <NavbarDivider />
                    <Popover
                        minimal
                        position={Position.BOTTOM_LEFT}
                        content={
                            <Menu>
                                <MenuItem
                                    icon={PROSPECTING_ICON}
                                    text="Prospecting"
                                    active={isProspecting}
                                    onClick={() =>
                                        navigate(`/${PROSPECTING_PATH}`)
                                    }
                                />
                                <MenuItem
                                    icon={IN_QUEUE_ICON}
                                    text="In-Queue"
                                    active={isInQueue}
                                    onClick={() =>
                                        navigate(`/${IN_QUEUE_PATH}`)
                                    }
                                />
                            </Menu>
                        }
                    >
                        <Button
                            icon={currentAppIcon}
                            text={currentAppLabel ?? "Select an app"}
                            rightIcon="caret-down"
                            className="PlatformTopBar-app-switcher"
                        />
                    </Popover>
                    {breadcrumbs.length > 0 && (
                        <Breadcrumbs
                            items={[...breadcrumbs]}
                            breadcrumbRenderer={(props: BreadcrumbProps) => (
                                <ReactRouterBreadcrumb {...props} />
                            )}
                        />
                    )}
                </NavbarGroup>
                <NavbarGroup align="right">
                    {actions.map(renderAction)}
                    <NavbarDivider />
                    <Popover
                        minimal
                        position={Position.BOTTOM_RIGHT}
                        content={
                            <Menu>
                                <MenuItem
                                    icon="credit-card"
                                    text="View plan"
                                    onClick={() => setPlanDialogOpen(true)}
                                />
                                <MenuDivider />
                                <MenuItem
                                    icon={IconNames.LOG_OUT}
                                    text="Logout"
                                    onClick={handleLogout}
                                />
                            </Menu>
                        }
                    >
                        <Button icon="user" minimal />
                    </Popover>
                </NavbarGroup>
            </Navbar>
            <PlanDetailsDialog
                isOpen={planDialogOpen}
                closeDialog={() => setPlanDialogOpen(false)}
            />
        </>
    );
};

const renderAction = (action: PlatformAction): React.ReactNode => {
    if (action.children !== undefined && action.children.length > 0) {
        return (
            <Popover
                key={action.label}
                minimal
                position={Position.BOTTOM_RIGHT}
                content={
                    <Menu className="PlatformTopBar-scrollable-menu">
                        {action.children.map(renderMenuItem)}
                    </Menu>
                }
            >
                <Tooltip content={action.label} position={Position.BOTTOM}>
                    <Button
                        icon={action.icon}
                        minimal
                        intent={action.intent}
                        disabled={action.disabled}
                    />
                </Tooltip>
            </Popover>
        );
    }
    return (
        <Tooltip
            key={action.label}
            content={action.label}
            position={Position.BOTTOM}
        >
            <Button
                icon={action.icon}
                minimal
                onClick={action.onClick}
                intent={action.intent}
                disabled={action.disabled}
            />
        </Tooltip>
    );
};

const renderMenuItem = (action: PlatformAction): React.ReactNode => {
    if (action.children !== undefined && action.children.length > 0) {
        return (
            <MenuItem
                key={action.label}
                icon={action.icon}
                text={action.label}
                intent={action.intent}
                disabled={action.disabled}
            >
                {action.children.map(renderMenuItem)}
            </MenuItem>
        );
    }
    return (
        <MenuItem
            key={action.label}
            icon={action.icon}
            text={action.label}
            onClick={action.onClick}
            intent={action.intent}
            disabled={action.disabled}
        />
    );
};

// Blueprint breadcrumbs use <a> tags by default; we wrap in <Link> for
// client-side navigation. The current breadcrumb gets a class to override
// the blue link color back to inherited text color.
const ReactRouterBreadcrumb: React.FC<BreadcrumbProps> = (props) =>
    props.href === undefined ? (
        <Breadcrumb {...props} />
    ) : (
        <Link
            to={props.href}
            className={
                props.current ? "PlatformTopBar-breadcrumb-current" : undefined
            }
        >
            <Breadcrumb {...props} />
        </Link>
    );
